import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import {
  fetchAdminStatus,
  fetchAdminUsers,
  fetchProfessionalRequests,
  fetchFlaggedReports,
  fetchAdminContacts,
  updateUserTier,
  updateUserTrust,
  blockUser,
  unblockUser,
  rejectProfessionalRequest,
  rejectReport,
  forceVoidContact,
  createCheckpoint,
  stampCheckpointAnchor,
  upgradeCheckpointAnchor,
  type UserSearchFilters,
  type AdminContactFilters,
} from '../api/admin'
import { fetchCheckpoints } from '../api/ledger'
import { useAuth } from './useAuth'

export function useAdminStatus() {
  const { token } = useAuth()
  return useQuery({
    queryKey: ['admin', 'status'],
    queryFn: () => fetchAdminStatus(token!),
    enabled: !!token,
    staleTime: 15_000,
  })
}

export function useAdminUsers(filters: UserSearchFilters) {
  const { token } = useAuth()
  return useQuery({
    queryKey: ['admin', 'users', filters],
    queryFn: () => fetchAdminUsers(token!, filters),
    enabled: !!token,
    staleTime: 10_000,
  })
}

export function useUpdateUserTier() {
  const { token } = useAuth()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, tier }: { userId: string; tier: string }) =>
      updateUserTier(token!, userId, tier),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'users'] })
      qc.invalidateQueries({ queryKey: ['admin', 'professional-requests'] })
    },
  })
}

export function useUpdateUserTrust() {
  const { token } = useAuth()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, trustMultiplier }: { userId: string; trustMultiplier: number }) =>
      updateUserTrust(token!, userId, trustMultiplier),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  })
}

export function useBlockUser() {
  const { token } = useAuth()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, reason }: { userId: string; reason: string }) =>
      blockUser(token!, userId, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'users'] })
      qc.invalidateQueries({ queryKey: ['admin', 'flagged-reports'] })
      qc.invalidateQueries({ queryKey: ['admin', 'contacts'] })
    },
  })
}

export function useUnblockUser() {
  const { token } = useAuth()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (userId: string) => unblockUser(token!, userId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  })
}

export function useProfessionalRequests() {
  const { token } = useAuth()
  return useQuery({
    queryKey: ['admin', 'professional-requests'],
    queryFn: () => fetchProfessionalRequests(token!),
    enabled: !!token,
    staleTime: 10_000,
  })
}

export function useRejectProfessionalRequest() {
  const { token } = useAuth()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (userId: string) => rejectProfessionalRequest(token!, userId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'professional-requests'] }),
  })
}

export function useFlaggedReports() {
  const { token } = useAuth()
  return useQuery({
    queryKey: ['admin', 'flagged-reports'],
    queryFn: () => fetchFlaggedReports(token!),
    enabled: !!token,
    staleTime: 10_000,
  })
}

export function useRejectReport() {
  const { token } = useAuth()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (reportId: string) => rejectReport(token!, reportId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'flagged-reports'] })
      qc.invalidateQueries({ queryKey: ['admin', 'contacts'] })
    },
  })
}

export function useAdminContacts(filters: AdminContactFilters) {
  const { token } = useAuth()
  return useQuery({
    queryKey: ['admin', 'contacts', filters],
    queryFn: () => fetchAdminContacts(token!, filters),
    enabled: !!token,
    staleTime: 10_000,
  })
}

export function useForceVoidContact() {
  const { token } = useAuth()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ contactId, reason }: { contactId: string; reason: string }) =>
      forceVoidContact(token!, contactId, reason),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'contacts'] }),
  })
}

export function useAdminCheckpoints() {
  return useQuery({
    queryKey: ['admin', 'checkpoints'],
    queryFn: () => fetchCheckpoints(),
    staleTime: 15_000,
  })
}

export function useCreateCheckpoint() {
  const { token } = useAuth()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => createCheckpoint(token!),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'checkpoints'] }),
  })
}

export function useStampCheckpointAnchor() {
  const { token } = useAuth()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (checkpointId: string) => stampCheckpointAnchor(token!, checkpointId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'checkpoints'] }),
  })
}

export function useUpgradeCheckpointAnchor() {
  const { token } = useAuth()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (checkpointId: string) => upgradeCheckpointAnchor(token!, checkpointId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'checkpoints'] }),
  })
}
